/**
 * Centralized logger utility
 * Prevents console statements in production and provides
 * integration with error tracking services (Sentry)
 */

import { captureException, captureMessage, addBreadcrumb } from './error-tracking'

const isDev = import.meta.env.DEV

interface LogContext {
  [key: string]: unknown
}

/**
 * Log an error message
 * Sends to Sentry in production
 */
export function logError(message: string, error?: Error | unknown, context?: LogContext): void {
  if (isDev) {
    console.error(`[ERROR] ${message}`, error, context)
  }

  // Send to error tracking service
  if (error instanceof Error) {
    captureException(error, { ...context, message })
  } else {
    captureMessage(`${message}: ${String(error)}`, 'error')
  }
}

/**
 * Log a warning message
 */
export function logWarn(message: string, context?: LogContext): void {
  if (isDev) {
    console.warn(`[WARN] ${message}`, context)
  }

  // Send warnings to monitoring service
  captureMessage(message, 'warning')
  if (context) {
    addBreadcrumb(message, context)
  }
}

/**
 * Log an info message
 */
export function logInfo(message: string, context?: LogContext): void {
  if (isDev) {
    console.log(`[INFO] ${message}`, context)
  }

  // Add breadcrumb for debugging context
  addBreadcrumb(message, context)
}

/**
 * Log a debug message (only in development)
 */
export function logDebug(message: string, context?: LogContext): void {
  if (isDev) {
    console.log(`[DEBUG] ${message}`, context)
    addBreadcrumb(message, context)
  }
}

/**
 * Grouped logger object for convenience
 */
export const logger = {
  error: logError,
  warn: logWarn,
  info: logInfo,
  debug: logDebug,
}
